/* eslint-disable no-unused-expressions */
import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import {
  Circle,
  InputContainer,
  MandatoryFieldText,
  SendToast,
  Text,
  Loading,
  Alert,
} from "@networkraildigitalfactory/components";
import { X } from "phosphor-react";

import { useMyPerformance, useObjectives, useEvidenceUpload } from "../../hooks/useAPI";
import ObjectiveInput from "./ObjectiveInput";
import { FileUpload } from "../FileUpload/FileUpload";
import { a11yGrowTextarea } from "../../services/utils";
import {
  addFiles,
  buildObjectiveAnswers,
  eoyReviewAlerts,
  generateFileBuffer,
  generateReviewTypeKey,
  getSubmissionConfig,
  removeFiles,
} from "./ReviewFormHelpers";
import ButtonRow from "./ButtonRow";
import EvidenceList from "../EvidenceList/EvidenceList";
import useUser from "../../hooks/useUser";
import { useFileDownload } from "../../hooks/useFileDownload";

import styles from "./CreateReviewPrep.module.css";

const ReviewPrepForm = (props) => {
  const { reviewType, isEditing, setIsDirtyForm, financialYear, user, setIsDraftPrep } = props;
  const { downloadFile } = useFileDownload();

  const history = useHistory();
  const { register, clearError, errors, getValues, triggerValidation, control, setValue } =
    useForm();

  const [loading, setLoading] = useState(false);
  const [objectives, setObjectives] = useState();
  const [file, hasFile] = useState([]);
  const [initialFiles, setInitialFiles] = useState([]);
  const [filesToDelete, setFilesToDelete] = useState([]);
  const [allUploaded, hasAllUploaded] = useState(false);
  const [prepFormData, setFormData] = useState([]);
  const reviewTypeLowerCase = reviewType.toLowerCase();
  const reviewTypePrep = generateReviewTypeKey(reviewTypeLowerCase, "Prep", true);
  const reviewTypeEvidence = generateReviewTypeKey(reviewTypeLowerCase, "Evidence", true);
  const isReviewTypePrepStarted = generateReviewTypeKey(reviewTypeLowerCase, "PrepStarted");
  const isReviewTypePrepCompleted = generateReviewTypeKey(reviewTypeLowerCase, "PrepCompleted");
  const isReviewTypeCompleted = generateReviewTypeKey(reviewTypeLowerCase, "Completed");
  const reviewTypeComments = generateReviewTypeKey(reviewTypeLowerCase, "Comments", true);
  const reviewTypeFeedback = generateReviewTypeKey(reviewTypeLowerCase, "Feedback", true);
  const reviewTypeRating = generateReviewTypeKey(reviewTypeLowerCase, "Rating", true);
  const reviewTypeProgress = generateReviewTypeKey(reviewTypeLowerCase, "Progress", true);
  const reviewTypeOverallComments = generateReviewTypeKey(
    reviewTypeLowerCase,
    "OverallComments",
    true
  );

  const interimEvidencePath = `${user.email}/${financialYear}/Interim/`;

  const { isGBR } = useUser();
  const { isGBR: isGBREmployee } = useUser(user.email);

  const [, getData] = useObjectives({ url: "review" }, { manual: true });

  const [, postReviewPrep] = useMyPerformance(
    {
      url: `${financialYear}/${user.email}`,
      method: "PUT",
    },
    { manual: true }
  );

  const [, postEvidence] = useEvidenceUpload(
    {
      url: `/upload?email=${user.email}&year=${financialYear}&formType=${reviewType}`,
      method: "POST",
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
    { manual: true, autoCancel: false }
  );

  const [, getFolderSize] = useEvidenceUpload(
    {
      url: `/checkFolderSize?email=${user.email}&year=${financialYear}&formType=${reviewType}`,
      method: "GET",
    },
    { manual: true, autoCancel: false }
  );

  const [, deleteInterimEvidence] = useEvidenceUpload({ method: "GET" }, { manual: true, autoCancel: false });

  const [currentPrep, setCurrentPrep] = useState();

  const [, getPrepByFinancialYear] = useMyPerformance(
    {
      url: `/${financialYear}/${user.email}`,
    },
    { manual: true }
  );

  const fetchPrepByFinancialYear = async () => {
    try {
      const prep = await getPrepByFinancialYear();
      if (prep?.data?.length > 0) {
        setCurrentPrep(prep.data[0]);
      } else {
        setCurrentPrep(null);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const reviewObjectives = await getData();
      setObjectives(reviewObjectives?.data);
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const sendPrep = async (formPayLoad) => {
    try {
      await postReviewPrep({
        data:
          formPayLoad && formPayLoad.hasOwnProperty("finalForm")
            ? formPayLoad.finalForm
            : prepFormData.finalForm,
      });
      setLoading(false);
      SendToast(
        formPayLoad && formPayLoad.hasOwnProperty("finalForm")
          ? formPayLoad.toastMsg
          : prepFormData.toastMsg,
        "success"
      );
      history.push(`/my-performance`);
    } catch (e) {
      console.error(e);
      SendToast("There was a problem submitting your prep", "error");
    }
  };

  useEffect(() => {
    if (allUploaded) {
      sendPrep();
    }
  }, [allUploaded, prepFormData]);

  useEffect(() => {
    if (isEditing || reviewType === "EOY") {
      fetchPrepByFinancialYear();
    }
  }, []);

  useEffect(() => {
    if (isEditing) {
      setIsDraftPrep(currentPrep?.[reviewTypeLowerCase]?.status === "draft");
    }
  }, [currentPrep]);

  const buildPayload = (data) => {
    const objectiveAnswers = buildObjectiveAnswers(data, reviewTypeLowerCase, currentPrep);
    const preppedEvidence = generateFileBuffer(data.evidence);
    let editedPrepEvidence;
    if (isEditing) {
      editedPrepEvidence = preppedEvidence.concat(
        currentPrep?.[reviewTypeLowerCase]?.[reviewTypePrep]?.[reviewTypeEvidence]
      );
    }

    const payload = {
      financialYear: financialYear.replace("-", "/"),
      email: user.email,
      objectives: objectiveAnswers,
      [reviewTypeLowerCase]: {
        [reviewTypePrep]: {
          [isReviewTypePrepCompleted]: data?.[isReviewTypePrepCompleted] || false,
          [isReviewTypePrepStarted]: true,
          [reviewTypeOverallComments]: data.overallComments,
          [reviewTypeEvidence]: isEditing ? editedPrepEvidence : preppedEvidence,
          [reviewTypeComments]: [],
        },
        [isReviewTypeCompleted]: false,
        status: data.status,
        [reviewTypeFeedback]: "",
        [reviewTypeRating]: "",
      },
    };
    return payload;
  };

  // Array to hold the file size values
  const fileSizeArray = [];
  // Variable which will determine if max of 10MB for total files is exceeded
  let totalSize = 0;
  const fizeSize = () => {
    if (file) {
      // eslint-disable-next-line array-callback-return
      file.map((f) => {
        // Map through the file array and push the file size to the new fileSizeArray
        fileSizeArray.push(f.size || f.fileSize);
      });
    }
    // for loop to sum the total size of file sizes
    for (let i = 0; i < fileSizeArray.length; i += 1) {
      totalSize += fileSizeArray[i];
    }
  };

  const fileUploadCheck = () => {
    fizeSize();

    let singleFile;
    let s3data = {};
    let uploadCount = 0;

    // eslint-disable-next-line array-callback-return
    if (file) {
      const filesToUpload = file.filter((item) => item.path);
      if (totalSize < 10000000) {
        if (filesToUpload.length > 0) {
          filesToUpload.map(async (f) => {
            singleFile = f;
            if (singleFile) {
              try {
                const postData = new FormData();
                postData.append("file", singleFile);
                const response = await getFolderSize();
                const { data } = response;
                if (Number(data) + Number(singleFile.size) > 10000000) {
                  SendToast(
                    "Previous evidence and current evidence exceeds 10MB. Please uploaded a smaller file",
                    "error"
                  );
                } else {
                  setLoading(true);
                  s3data = await postEvidence({
                    data: postData,
                  });

                  if (s3data) {
                    uploadCount += 1;
                    hasAllUploaded(uploadCount === filesToUpload.length);
                  } else {
                    hasAllUploaded(true);
                  }
                }
                SendToast("Evidence uploaded", "success");
              } catch (error) {
                SendToast(
                  "There was a problem uploading your evidence or file type is not allowed",
                  "error"
                );
                setLoading(false);
              }
            }
          });
        } else {
          hasAllUploaded(true);
        }
      } else {
        SendToast(
          "Total evidence exceeded max limit of 10MB, please upload smaller file(s)",
          "error"
        );
      }
    }

    return allUploaded;
  };

  const sendToCloud = async (type) => {
    const { status, toastMsg } = getSubmissionConfig(type);
    if (type !== "save") {
      await triggerValidation();
    }
    const isValid = await triggerValidation();
    if (type === "save" || isValid) {
      const formData = getValues();
      formData.status = status;
      if (type !== "save") {
        formData[isReviewTypePrepCompleted] = true;
      }
      formData.evidence = file;
      const finalForm = buildPayload(formData);

      const formPayLoad = {
        finalForm,
        toastMsg,
      };

      setFormData(formPayLoad);

      if (filesToDelete.length) {
        const deletePromises = filesToDelete.map((fileToDelete) => {
          return deleteInterimEvidence(
            {
              url: `/delete?email=${user.email}&year=${financialYear}&formType=${reviewType}&fileName=${fileToDelete.fileName}`,
              method: "GET",
            },
            { manual: true }
          );
        });

        try {
          await Promise.all(deletePromises);
        } catch (error) {
          console.error(error);
          SendToast("There was a problem submitting your prep evidence", "error");
        }
      }

      if (file && file.length > 0) {
        try {
          await fileUploadCheck();
        } catch (e) {
          console.error(e);
          SendToast("There was a problem submitting your prep evidence", "error");
        }
      } else {
        sendPrep(formPayLoad);
      }
    }
  };

  const onSave = async () => {
    sendToCloud("save");
  };

  const onSubmit = async () => {
    sendToCloud("submit");
  };

  const onEdit = async () => {
    sendToCloud("edit");
  };

  const onInputChange = () => {
    if (Object.keys(errors).length > 0) {
      triggerValidation();
    }
    setIsDirtyForm(true);
  };

  const onKeyDown = (e, field) => {
    if (e.key === "Enter") {
      const value = `${getValues()[field]}`;
      setValue(field, value);
    }
    a11yGrowTextarea(e, field);
  };

  useEffect(() => {
    const evidenceItems =
      currentPrep?.[reviewTypeLowerCase]?.[reviewTypePrep]?.[reviewTypeEvidence];

    hasFile(evidenceItems);

    // files uploaded to the s3 and saved in the db
    const stringifyEvidenceList = evidenceItems?.map((evidenceItem) =>
      JSON.stringify(evidenceItem)
    );
    setInitialFiles(stringifyEvidenceList);
  }, [currentPrep?.[reviewTypeLowerCase]?.[reviewTypePrep]?.[reviewTypeEvidence]]);

  return (
    <>
      <Loading loading={loading} />
      {!loading && reviewType && (
        <>
          <div className={styles.light_bg}>
            <Text wrapper="h1" type="header">
              {reviewType} review prep - {financialYear.replace("-", "/")}
            </Text>
            <div className={styles.vertical_padding}>
              <Text wrapper="p" type="body">
                {`Below you will find a list of your objectives${
                  reviewType === "EOY"
                    ? " as well as your interim review comments for each objective. You can add your end of year comment to each objective to document how you performed against that objective."
                    : ". You can add a comment to each objective to document your progress to achieving that objective."
                }`}
              </Text>
              <Text
                wrapper="p"
                type="body"
                style={{ margin: 0, color: "#EC1717", paddingBottom: "8px" }}
              >
                Please note:
              </Text>
              {isGBR ? (
                <Text wrapper="p" type="body" style={{ margin: 0, whiteSpace: "pre-wrap" }}>
                  Only approved objectives will show here. If you add a new objective after
                  submission, you will need to edit your prep to add comments for any new
                  objectives.{"\n\n"}Review your progress against your development, skills,
                  behaviours and objectives and upload evidence. To help with this you can complete
                  the behavioural framework. Click the{" "}
                  <a
                    target="_blank"
                    rel="noreferrer"
                    href="https://networkrail.sharepoint.com/:p:/r/sites/GBRTT-documents/_layouts/15/Doc.aspx?sourcedoc=%7BF3C10C22-BA7F-4E71-963E-140BE52DBD62%7D&file=Behaviours%20-%20Quick%20Reference%20Tool.pptx&action=edit&mobileredirect=true&xsdata=MDV8MDF8fDVlYmEwOTg5YzAxNDQ2ZTY2ZDdmMDhkYWY4YjI2ZmQzfGMyMmNjM2UxNWQ3ZjRmNGRiZTAzZDVhMTU4Y2M5NDA5fDB8MHw2MzgwOTU3NDI0OTAzNDA3MzV8VW5rbm93bnxWR1ZoYlhOVFpXTjFjbWwwZVZObGNuWnBZMlY4ZXlKV0lqb2lNQzR3TGpBd01EQWlMQ0pRSWpvaVYybHVNeklpTENKQlRpSTZJazkwYUdWeUlpd2lWMVFpT2pFeGZRPT18MXxNVFkzTXprM056UTBOemc1T1RzeE5qY3pPVGMzTkRRM09EazVPekU1T21GbE1ERTFaamxrTFRJeVlXWXROR0U0TlMxaU5EUTBMV1JsTWpCaU1XUXpNVEJpTTE5a01HWTBNV0U1TlMxallURmpMVFF5TURFdE9XVTJOQzB3T0dFMllUVm1NbVZsTURaQWRXNXhMbWRpYkM1emNHRmpaWE09fDc5MzQ1OTZjYzEwNDQ0YzYwYjMzMDhkYWY4YjI2ZmQxfDQxYTZiZWY4MjBhODQzYWFhOWY4MmYwNmJiZjlkYjE0&sdata=RnlDQUc4MkRRV0ZlbFJoNWNFZUtTeHZpdXZXMnhUM28xa09DTlQ4Q2lOcz0%3D&ovuser=c22cc3e1-5d7f-4f4d-be03-d5a158cc9409%2Ccbettis2%40networkrail.co.uk"
                  >
                    link
                  </a>{" "}
                  for the template.
                </Text>
              ) : (
                <Text wrapper="p" type="body" style={{ margin: 0 }}>
                  Only approved objectives will show here. If you add a new objective after
                  submission, you will need to edit your prep to add comments for any new
                  objectives.
                </Text>
              )}
            </div>
            <div className={styles.vertical_padding}>
              <MandatoryFieldText />
            </div>
            <div className={styles.vertical_padding}>
              {reviewType === "EOY" && eoyReviewAlerts(currentPrep)}
            </div>
            <form className={styles.review_form} role="presentation">
              {objectives && objectives.length === 0 ? (
                <div className={styles.noObjectives_row}>
                  <Circle color="#37519D" size={20}>
                    <X size={16} color="white" />
                  </Circle>
                  <Text
                    wrapper="h2"
                    type="header"
                    style={{ fontSize: "20px", margin: "0 0 0 10px" }}
                  >
                    No objectives found
                  </Text>
                </div>
              ) : (
                !loading &&
                objectives?.map((objective) => {
                  let defaultValue;
                  if (isEditing) {
                    const objectiveValue = currentPrep?.objectives?.filter(
                      (existingValue) => existingValue.objective._id === objective._id
                    );
                    defaultValue = objectiveValue && objectiveValue[0]?.[reviewTypeProgress];
                  }

                  let interimReviewComment = "";
                  if (
                    reviewType === "EOY" &&
                    currentPrep?.interim?.interimPrep?.isInterimPrepCompleted
                  ) {
                    const currentObjective = currentPrep?.objectives?.filter(
                      (currentPrepObjective) => currentPrepObjective.objective._id === objective._id
                    )[0];
                    interimReviewComment = currentObjective?.interimProgress || null;
                  }

                  const isNewObjective =
                    currentPrep?.objectives.length > 0 &&
                    !currentPrep?.objectives.some(
                      (currentPrepObjective) => currentPrepObjective.objective._id === objective._id
                    );

                  const canRemove =
                    !currentPrep?.interim?.isInterimCompleted ||
                    (currentPrep?.interim.isInterimCompleted && isNewObjective);

                  return (
                    <ObjectiveInput
                      reviewType={reviewType}
                      key={objective._id}
                      objective={objective}
                      inputRef={register({
                        required: "Must not be blank",
                      })}
                      interimReviewComment={interimReviewComment}
                      defaultValue={defaultValue}
                      error={errors[`objective${objective._id}`]}
                      onChange={onInputChange}
                      onKeyDown={onKeyDown}
                      canRemove={canRemove}
                      isNewObjective={isNewObjective}
                      isGBREmployee={isGBREmployee}
                    />
                  );
                })
              )}
              <Text wrapper="h2" type="header" style={{ fontSize: "20px" }}>
                Overall Comments
              </Text>
              {reviewType === "EOY" &&
                currentPrep?.interim?.interimPrep?.isInterimPrepCompleted &&
                currentPrep?.interim?.interimPrep?.interimOverallComments && (
                  <Alert
                    color="blue"
                    title="Interim review comment"
                    message={currentPrep?.interim?.interimPrep?.interimOverallComments}
                    style={{
                      margin: "24px 0",
                    }}
                  />
                )}
              <InputContainer
                labelText={`How have you found ${
                  reviewType === "EOY" ? "this performance year" : "the last 6 months"
                } ?`}
                forLabel="overallComments"
                required
                errorText={errors.overallComments && errors.overallComments.message}
              >
                <textarea
                  className={errors.overallComments ? styles.error : ""}
                  name="overallComments"
                  id="overallComments"
                  defaultValue={
                    currentPrep?.[reviewTypeLowerCase]?.[reviewTypePrep]?.[
                      reviewTypeOverallComments
                    ]
                  }
                  ref={register({
                    required: "Must not be blank",
                  })}
                  onChange={onInputChange}
                  onKeyDown={(e) => onKeyDown(e, "overallComments")}
                />
              </InputContainer>
              {reviewType === "EOY" &&
                currentPrep?.interim?.interimPrep?.isInterimPrepCompleted &&
                currentPrep?.interim?.interimPrep?.interimEvidence.length > 0 && (
                  <EvidenceList
                    style={{ marginBottom: "16px" }}
                    title="Interim evidence"
                    evidenceData={currentPrep?.interim?.interimPrep?.interimEvidence}
                    evidencePath={interimEvidencePath}
                    downloadFile={downloadFile}
                  />
                )}
              <Text wrapper="h3" type="header" style={{ fontSize: "20px" }}>
                {reviewType} evidence
              </Text>
              <InputContainer
                style={{ width: "200px" }}
                forLabel="evidence"
                errorText={errors.evidence && errors.evidence.message}
              >
                <Controller
                  as={
                    <FileUpload
                      onAddFiles={(value) => {
                        addFiles(value, file, hasFile);
                        setIsDirtyForm(true);
                      }}
                      onRemoveFiles={(event, fileToRemoveIndex, fileToDelete) => {
                        const wasFileSaved = initialFiles?.some(
                          (initialFile) => initialFile === JSON.stringify(fileToDelete)
                        );

                        if (wasFileSaved) {
                          setFilesToDelete((prevState) => [...prevState, fileToDelete]);
                        }

                        removeFiles(event, fileToRemoveIndex, clearError, file, hasFile);
                        setIsDirtyForm(true);
                      }}
                      files={file}
                    />
                  }
                  name="evidence"
                  valueName="evidence"
                  className={errors.evidence ? styles.error : ""}
                  type="file"
                  onChange={onInputChange}
                  control={control}
                />
              </InputContainer>
            </form>
          </div>
          <div className={styles.row}>
            <Text wrapper="p" type="body" style={{ margin: 0, padding: "24px 0 8px 0" }}>
              Once you’ve submitted your review, your manager will be able to see your comments,
              which will then be used in your interim / end of year review.
            </Text>
          </div>
          <ButtonRow
            isEditing={isEditing}
            isDraft={currentPrep?.[reviewTypeLowerCase]?.status === "draft"}
            onSave={onSave}
            onSubmit={onSubmit}
            onEdit={onEdit}
          />
        </>
      )}
    </>
  );
};

export default ReviewPrepForm;
